import formsUtils from '@/shared/Forms/forms.utils.ts'
import { AnyObject } from '@/global.types.ts'
import { Dispatch, SetStateAction, useCallback } from 'react'

interface IProps<
  T extends AnyObject,
  NestedElementsKey extends string,
  SourceObject extends { [key in NestedElementsKey]: T[] },
> {
  nestedArrayKey: NestedElementsKey
  sourceObjectSetter: Dispatch<SetStateAction<SourceObject>>

  newEntityGenerator?: () => T
}

const useNestedArrayChangeCallbacks = <
  T extends AnyObject,
  NestedElementsKey extends string,
  SourceObject extends { [key in NestedElementsKey]: T[] },
>(
  props: IProps<T, NestedElementsKey, SourceObject>
) => {
  const handleCreateNewElement = useCallback(() => {
    if (!props.newEntityGenerator) return

    props.sourceObjectSetter((prev) => ({
      ...prev,
      [props.nestedArrayKey]: [...prev[props.nestedArrayKey], props.newEntityGenerator()],
    }))
  }, [props.sourceObjectSetter, props.newEntityGenerator])

  const handleElementUpdate = useCallback(
    (index: number, value: any, key: string) => {
      props.sourceObjectSetter((prev) => ({
        ...prev,
        [props.nestedArrayKey]: formsUtils.arrays.updateItem(
          prev[props.nestedArrayKey],
          { [key]: value } as any,
          index
        ),
      }))
    },
    [props.sourceObjectSetter]
  )

  const handleElementDelete = useCallback(
    (index: number) => {
      props.sourceObjectSetter((prev) => ({
        ...prev,
        [props.nestedArrayKey]: formsUtils.arrays.removeItem(prev[props.nestedArrayKey], index),
      }))
    },
    [props.sourceObjectSetter]
  )

  return { handleCreateNewElement, handleElementUpdate, handleElementDelete }
}

export default useNestedArrayChangeCallbacks
